import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Activity, MapPin, CheckCircle } from 'lucide-react';
import { 
  fetchImagingTypeUsage, 
  fetchReferralPatternsByRegion, 
  fetchClinicalOutcomeMetrics 
} from '../../../services/analyticsService';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function UsageTrendsChart() {
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('imaging');
  const [periodDays, setPeriodDays] = useState(30);
  const [imagingData, setImagingData] = useState(null);
  const [regionalData, setRegionalData] = useState(null);
  const [outcomeData, setOutcomeData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalyticsData();
  }, [periodDays]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [imaging, regional, outcome] = await Promise.all([
        fetchImagingTypeUsage(periodDays),
        fetchReferralPatternsByRegion(periodDays),
        fetchClinicalOutcomeMetrics(periodDays)
      ]);

      setImagingData(imaging);
      setRegionalData(regional);
      setOutcomeData(outcome);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  const renderImagingTypeView = () => {
    if (!imagingData?.imagingTypes?.length) {
      return (
        <div className="text-center py-12 text-gray-500">
          No imaging data available for the selected period
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Imaging Type Distribution</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={imagingData?.imagingTypes}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ modality, percentage }) => `${modality}: ${percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
                nameKey="modality"
              >
                {imagingData?.imagingTypes?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Usage Statistics</h4>
          <div className="space-y-3">
            {imagingData?.imagingTypes?.slice(0, 5)?.map((type, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{type?.modality}</span>
                  <span className="text-sm text-gray-600">{type?.count} ({type?.percentage}%)</span>
                </div>
                <div className="text-xs text-gray-500">
                  Top regions: {type?.topBodyRegions?.map(r => r?.region)?.join(', ') || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderReferralPatternsView = () => {
    if (!regionalData?.regionalPatterns?.length) {
      return (
        <div className="text-center py-12 text-gray-500">
          No regional referral data available for the selected period
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Referrals by Region</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={regionalData?.regionalPatterns?.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="referrals" fill="#3B82F6" name="Referrals" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Regional Insights</h4>
          <div className="space-y-3">
            {regionalData?.regionalPatterns?.slice(0, 5)?.map((region, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-gray-900">{region?.country}</span>
                  </div>
                  <span className="text-sm text-gray-600">{region?.referrals} referrals ({region?.percentage}%)</span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Institutions: {region?.institutionCount}</div>
                  <div>Top specialties: {region?.topSpecialties?.map(s => s?.specialty)?.join(', ') || 'N/A'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderOutcomeMetricsView = () => {
    if (!outcomeData?.metrics) {
      return (
        <div className="text-center py-12 text-gray-500">
          No outcome metrics available for the selected period
        </div>
      );
    }

    const { metrics } = outcomeData;

    return (
      <div className="space-y-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Total Recommendations</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{metrics?.totalRecommendations}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Avg Confidence</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{(metrics?.avgConfidenceScore * 100)?.toFixed(1)}%</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Avg Rating</span>
            </div>
            <p className="text-2xl font-bold text-purple-900">{metrics?.avgFeedbackRating || 'N/A'}</p>
            <p className="text-xs text-purple-700">{metrics?.withFeedback} ratings</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-orange-900">High Confidence</span>
            </div>
            <p className="text-2xl font-bold text-orange-900">{metrics?.highConfidenceRate}%</p>
          </div>
        </div>

        {/* Rating Distribution & Modality Accuracy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Rating Distribution</h4>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={Object.entries(metrics?.ratingDistribution)?.map(([rating, count]) => ({
                rating: `${rating} ⭐`,
                count
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rating" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Modality Accuracy (by avg rating)</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {metrics?.modalityAccuracy?.map((modality, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-900">{modality?.modality}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-600">{modality?.recommendationCount} recs</span>
                    <span className="text-sm font-semibold text-purple-600">{modality?.avgRating} ⭐</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Usage Trends & Analytics</h3>
          
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e?.target?.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>

            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSelectedView('imaging')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  selectedView === 'imaging' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Imaging Types
              </button>
              <button
                onClick={() => setSelectedView('regional')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  selectedView === 'regional' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Regional
              </button>
              <button
                onClick={() => setSelectedView('outcomes')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  selectedView === 'outcomes' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Outcomes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {selectedView === 'imaging' && renderImagingTypeView()}
        {selectedView === 'regional' && renderReferralPatternsView()}
        {selectedView === 'outcomes' && renderOutcomeMetricsView()}
      </div>
    </div>
  );
}